import { ObservableMap } from '@stencil/store';
import { get } from '@uipath/portal-shell-util';

import { AuthSettings } from '../../constants/constants';
import { Account } from '../../models/account';
import { SessionSettings } from '../../models/SessionSettings';
import { isFalselyOrWhiteSpace } from '../../store/common';
import { State } from '../../store/types/state';
import {
    isMsi,
    isMsiOrStandalone,
} from '../EnvironmentUtil';
import { Features } from '../featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../featureFlags/featureFlags';
import { caseInsensitiveCompare } from '../helper';
import Logger from '../logger/logger';
import { getOrganizationStandardUrl } from '../orgStandardUrlHelper';

export const HOST_ACCOUNT = {
    organization: { name: 'host' },
    tenants: [],
    userGroups: [],
};

export async function refreshAccountCache(store: ObservableMap<State>, accessToken: string) {
    const isMSI = isMsi(store);
    const isK8sHost = store.get('isHost') && !isMSI;

    if (isMsiOrStandalone(store)) {
        const fallBackTenantList = [
            {
                name: store.get('currentTenant'),
                id: '',
                status: 'Enabled',
                enabledServices: store.get('overrideServices'),
                color: '',
                isCanaryTenant: false,
            },
        ];

        if (
            (isMSI || isK8sHost) && (
                getFeatureFlag(Features.EnablePortalShellIdleTimeout.name) || getFeatureFlag(Features.EnablePortalShellSingleSession.name)
            ) && accessToken
        ) {
            const sessionSettings = await getSessionSettingsForMsi(accessToken, store.get('organizationId'));
            Logger.info([ 'api', 'msi' ], 'session settings fetch success', { object: sessionSettings });
            store.set('account', {
                organization: {
                    ...store.state.account?.organization ?? { name: isK8sHost ? 'host' : 'msi' },
                    ...sessionSettings,
                },
                tenants: isK8sHost ? [] : fallBackTenantList,
                userGroups: [],
            });
        } else {
            store.set('tenantList', fallBackTenantList);
            store.set('currentTenantInfo', fallBackTenantList[0]);
        }
        return;
    }

    const organizationStandardUrl = getOrganizationStandardUrl(store);
    Logger.debug('api', `organizationStandardUrl: ${organizationStandardUrl}, IsTokenValid: ${!isFalselyOrWhiteSpace(accessToken)}`, { state: store.state });

    if (organizationStandardUrl && accessToken) {
        const account = await getAccount(organizationStandardUrl, accessToken);
        Logger.info('api', 'account fetch success', {
            object: account,
            state: store.state,
        });
        store.set('account', account);
    }
}

async function getSessionSettingsForMsi(token: string, organizationId: string): Promise<SessionSettings> {
    const sessionSettingsUrl = `${window.location.origin}/identity/api/Setting?key=${AuthSettings.IdleTimeoutDuration}&key=${AuthSettings.AllowConcurrentBrowserSessions}&partitionGlobalId=${organizationId}`;

    Logger.debug([ 'api', 'msi' ], 'fetching session settings');
    const sessionSettings: Array<{ key: string; value: string }> | undefined = await get(
        sessionSettingsUrl,
        { token },
        (e, method, url, requestProps) => {
            Logger.error([ 'api', 'msi' ], `unexpected error happened on session settings API call: ${e}, ${e.stack}`, {
                object: {
                    method,
                    error: e,
                    url,
                    requestProps,
                    hasToken: !!token,
                },
                stack: e.stack,
            });
        },
    );

    const idleSessionTimeoutPeriod = sessionSettings?.find(
        (setting) => caseInsensitiveCompare(setting.key, AuthSettings.IdleTimeoutDuration),
    )?.value;
    const allowConcurrentBrowserSessions = sessionSettings?.find(
        setting => caseInsensitiveCompare(setting.key, AuthSettings.AllowConcurrentBrowserSessions),
    )?.value ?? 'true';

    return {
        idleSessionTimeoutPeriod: Math.min(
            // if this is not a number, it will return NaN
            Number(idleSessionTimeoutPeriod), 24 * 60 * 60 * 1000,
        ) || undefined,
        allowConcurrentSessions: allowConcurrentBrowserSessions,
    };
}

async function getAccount(organizationStandardUrl: string, token: string): Promise<Account | undefined> {
    const accountUrl = `${organizationStandardUrl}/portal_/api/filtering/leftnav/tenantsAndOrganizationInfo`;

    Logger.debug('api', 'fetching account');
    return get(
        accountUrl,
        { token },
        (e, method, url, requestProps) => {
            Logger.error('api', `unexpected error happened on Account API call: ${e}, ${e.stack}`, {
                object: {
                    method,
                    error: e,
                    url,
                    requestProps,
                    hasToken: !!token,
                },
                stack: e.stack,
            });
        },
    );
}
